import { useMemo, useState, type ReactNode, useRef, useEffect } from 'react';

import Text from '../../ui/Text/Text';
import { useScrollToTopOnMount } from '../../hooks/useScrollToTopOnMount';
import Icon from '../../ui/Icon/Icon';

import './orgInfo.scss';
import useDeviceTypeDetector from '../../hooks/useDeviceTypeDetector/useDeviceTypeDetector';
import { TIconName } from '../../ui/Icon/Icon.types';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { AccessibilityModeEnum } from '../../utils/enums';
import Image from '../../ui/Image/Image';

import bgLarge from '../../assets/media/orgInfo/orgInfoBg_Large.png';
import bgMedium from '../../assets/media/orgInfo/orgInfoBg_Medium.png';
import bgSmall from '../../assets/media/orgInfo/orgInfoBg_Small.png';

type OrgInfoItemBase = {
  id: string;
  title?: string;
  description?: ReactNode;
};

type OrgInfoTextItem = OrgInfoItemBase & {
  type: 'text';
  strong?: boolean;
  content: ReactNode;
};

type OrgInfoInteractiveItem = OrgInfoItemBase & {
  type: 'link' | 'resource' | 'file';
  content: string;
  label?: string;
  href: string;
  external?: boolean;
  hasIcon?: boolean;
  target?: string;
};

type OrgInfoItem = OrgInfoTextItem | OrgInfoInteractiveItem;

type OrgInfoSection = {
  id: string;
  title: string;
  description?: string;
  items: OrgInfoItem[];
};

const orgInfoSections: OrgInfoSection[] = [
  {
    id: 'main-info',
    title: 'Основные сведения',
    items: [
      {
        id: 'name',
        type: 'text',
        content: 'Полное наименование: Общество с ограниченной ответственностью «СКАЙБОКС»',
      },
      {
        id: 'short-name',
        type: 'text',
        content: 'Сокращенное наименование: ООО «СКАЙБОКС»',
      },
      {
        id: 'create-date',
        type: 'text',
        content: 'Дата регистрации организации — 21.09.2020 год',
      },
      {
        id: 'main-info-owner',
        type: 'text',
        content: 'Учредителем ООО «СКАЙБОКС» является Товмасян Асмик Рубиковна.',
      },
      {
        id: 'main-info-address',
        type: 'text',
        content:
          'Юридический адрес ООО «СКАЙБОКС»: 162603, Вологодская область, г. Череповец, ул. Архангельская, д. 100.',
      },
      {
        id: 'main-info-domain',
        type: 'text',
        content:
          'ООО «СКАЙБОКС» осуществляет образовательную деятельность на основании лицензии № Л035-01249-35/00230953, выданной 26 октября 2021 года Министерством образования Вологодской области, на ведение образовательной деятельности по реализации программ дополнительного образования (дополнительное образование детей и взрослых: дополнительное профессиональное образование) и профессиональному обучению.',
      },
      {
        id: 'main-info-edu-method',
        type: 'text',
        content: 'Образовательная деятельность осуществляется посредством электронного обучения.',
      },
      {
        id: 'main-info-schedule',
        type: 'text',
        content: 'Режим работы: с понедельника по пятницу с 09:00 до 18:00. ',
      },
      {
        id: 'main-info-phone',
        type: 'link',
        label: 'Контактные номера телефонов:',
        content: '+7-921-053-13-37.',
        href: 'tel:79210531337',
      },
      {
        id: 'main-info-email',
        type: 'link',
        label: 'Контактная электронная почта:',
        content: 'arshakz@inbox.ru.',
        href: 'mailto:arshakz@inbox.ru',
      },

      {
        id: 'main-info-doc',
        type: 'link',
        href: '/org-info/main-info/Выписка_из_реестра_лицензий_ООО_СКАЙБОКС.pdf',
        content: 'Выписка из реестра лицензий ООО СКАЙБОКС',
        target: '_blank',
        hasIcon: true,
      },
    ],
  },
  {
    id: 'structure',
    title: 'Структура и органы управления образовательной организацией',
    items: [
      {
        id: 'manager',
        type: 'text',
        content:
          'Руководителем ООО «СКАЙБОКС» является Товмасян Асмик Рубиковна (генеральный директор).',
      },
    ],
  },
  {
    id: 'documents',
    title: 'Документы',
    items: [
      {
        id: 'doc-1',
        type: 'link',
        href: '/org-info/documents/1_Правила_приема_обучающихся_в_Учебный_центр.pdf',
        content: 'Правила приема обучающихся в Учебный центр',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-2',
        type: 'link',
        href: '/org-info/documents/2_Правила_внутреннего_трудового_распорядка_работников.pdf',
        content: 'Правила внутреннего трудового распорядка работников',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-3',
        type: 'link',
        href: '/org-info/documents/3_Правила_внутреннего_распорядка_обучающихся.pdf',
        content: 'Правила внутреннего распорядка обучающихся',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-4',
        type: 'link',
        href: '/org-info/documents/4_Порядок_оформления_возникновения,_приостановления_и_прекращения.pdf',
        content: 'Порядок оформления возникновения, приостановления и прекращения',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-5',
        type: 'link',
        href: '/org-info/documents/5_Порядок_и_основания_перевода,_отчисления_и_восстановления_обучающихся.pdf',
        content: 'Порядок и основания перевода, отчисления и восстановления обучающихся',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-6',
        type: 'link',
        href: '/org-info/documents/6_Положение_об_организации_электронного_обучения,_применении_дистанционных.pdf',
        content: 'Положение об организации электронного обучения, применении дистанционных',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-7',
        type: 'link',
        href: '/org-info/documents/7_Положение_о_порядке_проведения_контроля_знаний_обучающихся.pdf',
        content: 'Положение о порядке проведения контроля знаний обучающихся',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-8',
        type: 'link',
        href: '/org-info/documents/8_Положение_о_документах_об_обучении,_о_квалификации.pdf',
        content: 'Положение о документах об обучении, о квалификации',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-9',
        type: 'link',
        href: '/org-info/documents/9_Положение_о_системе_учета_и_хранения_результатов_обучения.pdf',
        content: 'Положение о системе учета и хранения результатов обучения',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-10',
        type: 'link',
        href: '/org-info/documents/Устав ООО СКАЙБОКС.pdf',
        content: 'Устав ООО СКАЙБОКС',
        target: '_blank',
        hasIcon: true,
      },
    ],
  },
  {
    id: 'education',
    title: 'Образование',
    items: [
      {
        id: 'education-intro',
        type: 'text',
        content: [
          'ООО «СКАЙБОКС» имеет лицензию на реализацию образовательных программа по следующим видам образования:',
          '<span>1. Дополнительное образование – подвиды: дополнительное образование детей и взрослых; дополнительное профессиональное образование;</span>',
          '<span>2. Профессиональное обучение.</span>',
        ].join('\n'),
      },
      {
        id: 'education-about',
        type: 'text',
        content:
          'На данный момент ООО «СКАЙБОКС» осуществляет образовательную деятельность только по дополнительному образованию – подвид дополнительное образование детей и взрослых.',
      },
      {
        id: 'education-method',
        type: 'text',
        content:
          'ООО «СКАЙБОКС» проводит обучение посредством электронного обучения в заочной форме на русском языке.',
      },
      {
        id: 'education-programs',
        type: 'text',
        content: [
          'ООО «СКАЙБОКС» реализует следующие образовательные программы:',
          '<span>1. Дополнительная общеобразовательная общеразвивающая программа социально-гуманитарной направленности «Business camp: мой первый бизнес».</span>',
        ].join('\n'),
      },
      {
        id: 'education-note',
        type: 'text',
        content:
          'Нормативный срок обучения определен образовательной программой.\n' +
          'Государственная аккредитация образовательной деятельности отсутствует.',
      },
      {
        id: 'education-doc',
        type: 'link',
        href: '/org-info/education/Учебная_программа_Business_Camp_мой_первый_бизнес.pdf',
        content: 'Учебная программа Business Camp мой первый бизнес',
        target: '_blank',
        hasIcon: true,
      },
    ],
  },
  {
    id: 'management',
    title: 'Руководство',
    items: [
      {
        id: 'manager-full',
        type: 'text',
        content:
          'Руководитель образовательной организации – генеральный директор ООО «СКАЙБОКС» Товмасян Асмик Рубиковна.',
      },
      {
        id: 'phone',
        type: 'link',
        label: 'Контактный номер телефона:',
        content: '7-921-053-13-37.',
        href: 'tel:79210531337',
      },
      {
        id: 'email',
        type: 'link',
        label: 'Контактная электронная почта:',
        content: 'arshakz@inbox.ru.',
        href: 'mailto:arshakz@inbox.ru',
      },
      {
        id: 'management-note',
        type: 'text',
        content: 'Заместители руководителя отсутствуют.',
      },
    ],
  },
  {
    id: 'stuff',
    title: 'Педагогический состав',
    items: [
      {
        id: 'stuff-title',
        type: 'text',
        strong: true,
        content: 'Состав педагогических работников ООО «СКАЙБОКС» представлен в лице:',
      },
      // {
      //   id: 'blabla',
      //   type: 'text',
      //   content: [
      //     'Заргарян Аршак Месропович, педагог дополнительного образования',
      //     'Уровень образования: высшее образование – кандидат экономических наук',
      //     'Квалификация: менеджер',
      //     'Наименование направления подготовки и (или) специальности: менеджмент организации (предпринимательство)',
      //     'Наименование учебного заведения: Санкт-Петербургский государственный инженерно-экономический университет',
      //     'Программы, в реализации которых участвует педагогический работник: Дополнительная общеобразовательная общеразвивающая программа социально-гуманитарной направленности «Business Camp: мой первый бизнес»',
      //     'Опыт работы – 14 лет',
      //   ].join('\n')
      // },
      {
        id: 'stuff-a',
        type: 'text',
        content: 'Заргарян Аршак Месропович, педагог дополнительного образования',
      },
      {
        id: 'stuff-b',
        type: 'text',
        content: 'Уровень образования: высшее образование – кандидат экономических наук',
      },
      {
        id: 'stuff-c',
        type: 'text',
        content: 'Квалификация: менеджер',
      },
      {
        id: 'stuff-d',
        type: 'text',
        content:
          'Наименование направления подготовки и (или) специальности: менеджмент организации (предпринимательство)',
      },
      {
        id: 'stuff-e',
        type: 'text',
        content:
          'Наименование учебного заведения: Санкт-Петербургский государственный инженерно-экономический университет',
      },
      {
        id: 'stuff-f',
        type: 'text',
        content:
          'Повышение квалификации: Удостоверение о повышении квалификации рег. № 2026/03/039, выданное 13.03.2026 года OOO "Информационно-коммуникативные технологии плюс " по программе "Финансовое консультирование".',
      },
      {
        id: 'stuff-g',
        type: 'text',
        content:
          'Программы, в реализации которых участвует педагогический работник: Дополнительная общеобразовательная общеразвивающая программа социально-гуманитарной направленности «Business Camp: мой первый бизнес»',
      },
      {
        id: 'stuff-j',
        type: 'text',
        content: 'Опыт работы – 14 лет ',
      },
    ],
  },
  {
    id: 'tech-supply',
    title:
      'Материально-техническое обеспечение и оснащенность образовательного процесса. Доступная среда.',
    items: [
      {
        id: 'tech-supply-a',
        type: 'text',
        content:
          'ООО «СКАЙБОКС» реализует образовательные программы посредством электронного обучения. Учебный процесс и его сопровождение проводится на электронной информационной образовательной среде “Get Course”',
      },
      {
        id: 'tech-supply-b',
        type: 'text',
        content:
          'Для освоения учебных предметов, дисциплин (модулей) обучающиеся имеют право бесплатно получать доступ к учебным материалам, пособиям в электронной формате на срок равный периоду обучения по выбранной образовательной программе.',
      },
      {
        id: 'tech-supply-c',
        type: 'text',
        content:
          'Проведение занятий проходит посредством электронного обучения, что дает возможность проходить обучение инвалидам и лицам с ограниченными возможностями здоровья.',
      },
    ],
  },
  {
    id: 'educational-services',
    title: 'Платные образовательные услуги',
    items: [
      {
        id: 'price-list-doc',
        type: 'link',
        href: '/org-info/educational-services/PriceList.pdf',
        content: 'Прайс лист',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'lifecycle-rules',
        type: 'link',
        href: '/org-info/educational-services/Порядок_оформления_возникновения,_приостановления_и_прекращения.pdf',
        content: 'Порядок оформления возникновения, приостановления и прекращения',
        target: '_blank',
        hasIcon: true,
      },
      {
        id: 'doc-11',
        type: 'link',
        href: '/org-info/educational-services/Договор_оферта_на_оказание_платных_образовательных_услуг.pdf',
        content: 'Договор оферта на оказание платных образовательных услуг',
        target: '_blank',
        hasIcon: true,
      },
    ],
  },
  {
    id: 'domain',
    title: 'Финансово-хозяйственная деятельность',
    items: [
      {
        id: 'domain-point-a',
        type: 'text',
        content:
          'Финансовое обеспечение за счет бюджетных ассигнований федерального бюджета, бюджетов субъектов российской Федерации, местных бюджетов не предусмотрено.',
      },
      {
        id: 'domain-point-b',
        type: 'text',
        content:
          'Финансирование по договорам об оказании платных образовательных услуг за счет физических лиц неограниченно.',
      },
    ],
  },
  {
    id: 'vacances',
    title: 'Вакантные места для приема (перевода) обучающихся',
    items: [
      {
        id: 'vacances-recruitment',
        type: 'text',
        content:
          'Индивидуальное/групповое обучение по реализуемым образовательным программам оформляется на основании заявлений физических лиц.',
      },
      {
        id: 'vacances-count',
        type: 'text',
        content:
          'Актуальное количество оставшихся вакантных мест на обучение следует уточнять по адресу электронной почты: arshakz@inbox.ru',
      },
    ],
  },
  {
    id: 'scholarships',
    title: 'Стипендии и меры поддержки обучающихся',
    items: [
      {
        id: 'support',
        type: 'text',
        content:
          'Стипендии и иные меры поддержки для обучающихся в ООО «СКАЙБОКС» не предусмотрены, общежитие/интернат не предоставляется.',
      },
    ],
  },
  {
    id: 'multinational',
    title: 'Международное сотрудничество',
    items: [
      {
        id: 'docs',
        type: 'text',
        content:
          'Заключенные и планируемые к заключению договоры об оказании платных образовательных услуг с иностранными/международными организациями по вопросам образования и науки в ООО «СКАЙБОКС» отсутствуют.',
      },
    ],
  },
  {
    id: 'food',
    title: 'Организация питания в образовательной организации',
    items: [
      {
        id: 'food-point-a',
        type: 'text',
        content:
          'Обеспечение питанием обучающихся не осуществляется. Образовательный процесс в ООО «СКАЙБОКС» реализуется посредством электронного обучения, без присутствия обучающихся в помещениях Учебного центра.',
      },
    ],
  },
];

const itemTypeMeta: Record<
  OrgInfoItem['type'],
  { label: string; icon?: { name: TIconName; size: 20 | 24; asVector?: boolean } }
> = {
  text: { label: 'Текст' },
  link: { label: 'Ссылка', icon: { name: 'link', size: 24 } },
  resource: { label: 'Ресурс', icon: { name: 'download', size: 24 } },
  file: { label: 'Файл', icon: { name: 'pdf', size: 24 } },
};

const OrgInfo = () => {
  useScrollToTopOnMount();

  const tabPanelRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);

  const { isTablet, isMobile } = useDeviceTypeDetector();
  const {
    settings: { mode },
  } = useAccessibility();

  const [tabPanelScrollState, setTabPanelScrollState] = useState<'top' | 'middle' | 'bottom'>(
    'top'
  );
  const [activeSectionId, setActiveSectionId] = useState(orgInfoSections[0].id);

  const activeSection = useMemo(
    () => orgInfoSections.find((section) => section.id === activeSectionId) ?? orgInfoSections[0],
    [activeSectionId]
  );

  useEffect(() => {
    const tabPanel = tabPanelRef.current;
    if (!tabPanel) return;

    const scrollThreshold = 20;
    const updateScrollState = () => {
      const maxScrollTop = tabPanel.scrollHeight - tabPanel.clientHeight;
      const nextState =
        maxScrollTop <= 0 || tabPanel.scrollTop <= scrollThreshold
          ? 'top'
          : maxScrollTop - tabPanel.scrollTop <= scrollThreshold
            ? 'bottom'
            : 'middle';

      setTabPanelScrollState((prevState) => (prevState === nextState ? prevState : nextState));
    };

    updateScrollState();
    tabPanel.addEventListener('scroll', updateScrollState, { passive: true });

    return () => {
      tabPanel.removeEventListener('scroll', updateScrollState);
    };
  }, []);

  useEffect(() => {
    panelRef.current?.scrollTo({ top: 0 });
  }, [activeSectionId]);

  const handleTabPanelScroll = (direction: 'up' | 'down') => {
    const tabPanel = tabPanelRef.current;
    if (!tabPanel) return;

    const scrollOffset = Math.max(120, Math.floor(tabPanel.clientHeight * 0.8));
    tabPanel.scrollBy({
      top: direction === 'up' ? -scrollOffset : scrollOffset,
      behavior: 'smooth',
    });
  };

  const handleTabChange = (id: string) => {
    setActiveSectionId(id);

    if (!isMobile && !isTablet) {
      pageRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <main className='page org-info-page' ref={pageRef}>
      <section className='org-info-details'>
        <div className='container'>
          <div className='org-info-shell'>
            <div className='org-info-hero'>
              <div className='org-info-heading'>
                <Text variant='h3' fontWeight={600}>
                  Сведения об образовательной организации
                </Text>
              </div>
            </div>
            <div className='org-info-tabs-wrapper' data-scroll-state={tabPanelScrollState}>
              <button
                type='button'
                className='org-info-tab-scroll-btn org-info-tab-scroll-btn--up'
                aria-label='Прокрутить вверх'
                onClick={() => handleTabPanelScroll('up')}
                disabled={tabPanelScrollState === 'top'}
              >
                <Icon className='org-info-tab-scroll-icon' name='chevronRight' size={16} />
              </button>

              <div
                ref={tabPanelRef}
                className='org-info-tabs'
                role='tablist'
                aria-label='Разделы сведений об организации'
              >
                {orgInfoSections.map((section) => {
                  const isActive = section.id === activeSectionId;
                  const tabId = `org-info-tab-${section.id}`;
                  const panelId = `org-info-panel-${section.id}`;
                  const textVariant = isTablet ? 'body3' : 'body2';

                  return (
                    <button
                      key={section.id}
                      type='button'
                      role='tab'
                      aria-selected={isActive}
                      className='org-info-tab'
                      id={tabId}
                      aria-controls={panelId}
                      onClick={() => handleTabChange(section.id)}
                    >
                      <Text variant={textVariant} fontWeight={400}>
                        {section.title}
                      </Text>
                    </button>
                  );
                })}
              </div>
              <button
                type='button'
                className='org-info-tab-scroll-btn org-info-tab-scroll-btn--down'
                aria-label='Прокрутить вниз'
                onClick={() => handleTabPanelScroll('down')}
                disabled={tabPanelScrollState === 'bottom'}
              >
                <Icon className='org-info-tab-scroll-icon' name='chevronRight' size={16} />
              </button>
            </div>

            <div
              ref={panelRef}
              className='org-info-panel'
              role='tabpanel'
              id={`org-info-panel-${activeSection.id}`}
              aria-labelledby={`org-info-tab-${activeSection.id}`}
            >
              <Text variant={isMobile || isTablet ? 'subtitle3' : 'subtitle2'} fontWeight={500}>
                {activeSection.title}
              </Text>
              {/* {activeSection.description && (
                <Text variant='body1' className='org-info-description'>
                  {activeSection.description}
                </Text>
              )} */}

              <div className='org-info-items'>
                {activeSection.items.map((item) => {
                  const meta = itemTypeMeta[item.type];

                  return (
                    <div className='org-info-item' key={item.id} data-type={item.type}>
                      <div className='org-info-item-header'>
                        <Text variant='subtitle3' fontWeight={500} className='org-info-item-title'>
                          {item.title}
                        </Text>
                      </div>
                      {/* {item.description && (
                        <Text variant='body2' className='org-info-item-description'>
                          {item.description}
                        </Text>
                      )} */}
                      <div className='org-info-item-content'>
                        {item.type === 'text' ? (
                          <div className='org-info-item-row'>
                            {mode !== AccessibilityModeEnum.Enhanced && meta.icon && (
                              <Icon name={meta.icon.name} size={meta.icon.size} />
                            )}
                            <Text
                              className='item-inner item-link'
                              variant='body1'
                              fontWeight={item?.strong ? 500 : 400}
                            >
                              {item.content}
                            </Text>
                          </div>
                        ) : (
                          <div className='org-info-item-row'>
                            {!!item.label && <Text variant='body1'>{item.label}</Text>}
                            {mode !== AccessibilityModeEnum.Enhanced &&
                              meta.icon &&
                              item.hasIcon && <Icon name={meta.icon.name} size={meta.icon.size} />}
                            <a
                              className='item-inner item-link'
                              href={item.href}
                              target={item.target ?? undefined}
                              rel={item.external ? 'noreferrer' : undefined}
                              {...(item.type === 'file'
                                ? { download: !item.external ? '' : undefined }
                                : {})}
                            >
                              <Text variant='body1' fontWeight={500}>
                                {item.content}
                              </Text>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className='container org-info-bg'>
        <Image alt='Org info banner' src={isMobile ? bgSmall : isTablet ? bgMedium : bgLarge} />
      </section>
    </main>
  );
};

export default OrgInfo;
